import {
  ThresholdBar,
  type ThresholdMarker,
} from '../../../core/components/threshold-bar/ThresholdBar.js';
import { getMarkerValue } from '../../../core/components/threshold-bar/utils/get-marker-value.js';
import { getThresholdPixel } from '../../../core/components/threshold-bar/utils/get-threshold-pixel.ts.js';
import { isStatic } from '../../../core/types/type-guards.js';
import { useSetState } from '../../hooks/store/selectors.js';
import { useCategoricalBarChartBaseConfig } from '../../hooks/useCategoricalBarChartBaseConfig.js';
import { useCategoricalBarChartScales } from '../../hooks/useCategoricalBarChartScales.js';
import { useCategoricalBarChartThresholds } from '../../hooks/useCategoricalBarChartThresholds.js';
import { useFormatter } from '../../hooks/useFormatter.js';

interface CategoricalThresholdBarProps {
  length: number;
}

export const CategoricalThresholdBar = ({
  length,
}: CategoricalThresholdBarProps) => {
  const { layout } = useCategoricalBarChartBaseConfig();
  const { valueScale } = useCategoricalBarChartScales();
  const setState = useSetState();
  const formatter = useFormatter();
  const thresholds = useCategoricalBarChartThresholds();

  const handleHover = (id: string | undefined) => {
    setState((prev) => ({
      ...prev,
      hoveredThresholdId: id,
    }));
  };

  const getMarkerProps = (yFirst: number, yLast: number) => ({
    y0: getThresholdPixel(yFirst, valueScale, layout),
    y1: getThresholdPixel(yLast, valueScale, layout),
    value: getMarkerValue({ y0: yFirst, y1: yLast }, formatter),
  });

  const markers: ThresholdMarker[] = thresholds.map((threshold) => {
    if (isStatic(threshold.data)) {
      return {
        ...threshold,
        ...getMarkerProps(threshold.data.value, threshold.data.value),
      };
    }
    return {
      ...threshold,
      ...getMarkerProps(threshold.data.min, threshold.data.max),
    };
  });

  return markers.length ? (
    <ThresholdBar
      markers={markers}
      length={length}
      layout={layout}
      onHover={handleHover}
    />
  ) : null;
};

CategoricalThresholdBar['displayName'] = 'TimeseriesThresholdBar';
